import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  ArrowLeft,
  User,
  FileText,
  Gavel,
  Upload,
  MessageSquare,
  CheckSquare,
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Target,
  Activity,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700' },
  in_progress: { label: 'In Progress', color: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-700' },
};

const StaffCaseDetailPage = () => {
  const { caseId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/case-modules/case/${caseId}/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverview(response.data);
    } catch (err) {
      console.error('Failed to fetch case overview:', err);
      setError(err.response?.data?.detail || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && caseId) fetchOverview();
  }, [token, caseId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/staff/cases')}>
          <ArrowLeft size={18} className="mr-2" />
          Back to Cases
        </Button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  const { case: caseData, modules, workflow, access } = overview || {};
  const status = statusConfig[caseData?.status] || statusConfig.active;

  return (
    <div className="space-y-6" data-testid="staff-case-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/staff/cases')}>
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Case Details</h1>
            <p className="text-slate-500 mt-1">{caseId}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${status.color}`}>
            {status.label}
          </span>
          <Button onClick={fetchOverview} variant="outline" size="sm">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Client Info */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
            <User size={28} className="text-slate-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-stone-900">
              {caseData?.client_user_id || 'Client'}
            </h2>
            <p className="text-sm text-slate-500">
              Case Type: {caseData?.case_type?.replace('_', ' ') || 'Credit Repair'}
            </p>
          </div>
          <div className="text-right text-sm text-slate-500">
            <p>Created: {caseData?.created_at ? new Date(caseData.created_at).toLocaleDateString() : 'N/A'}</p>
            <p>Updated: {caseData?.updated_at ? new Date(caseData.updated_at).toLocaleDateString() : 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <FileText size={24} className="mx-auto text-slate-400 mb-2" />
          <p className="text-2xl font-bold text-stone-900">{modules?.reports || 0}</p>
          <p className="text-xs text-slate-500">Reports</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <Target size={24} className="mx-auto text-amber-500 mb-2" />
          <p className="text-2xl font-bold text-stone-900">{modules?.dispute_candidates || 0}</p>
          <p className="text-xs text-slate-500">Candidates</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <Gavel size={24} className="mx-auto text-emerald-500 mb-2" />
          <p className="text-2xl font-bold text-stone-900">{modules?.disputes || 0}</p>
          <p className="text-xs text-slate-500">Disputes</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <Upload size={24} className="mx-auto text-amber-500 mb-2" />
          <p className="text-2xl font-bold text-stone-900">{modules?.uploads || 0}</p>
          <p className="text-xs text-slate-500">Uploads</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <MessageSquare size={24} className="mx-auto text-emerald-500 mb-2" />
          <p className="text-2xl font-bold text-stone-900">{modules?.messages || 0}</p>
          <p className="text-xs text-slate-500">Messages</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <CheckSquare size={24} className="mx-auto text-slate-500 mb-2" />
          <p className="text-2xl font-bold text-stone-900">{modules?.tasks || 0}</p>
          <p className="text-xs text-slate-500">Tasks</p>
        </div>
      </div>

      {/* Workflow Status */}
      {workflow && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Workflow Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {workflow.needs_attention && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <AlertCircle className="text-amber-600 mb-2" size={20} />
                <p className="font-medium text-amber-800">Needs Attention</p>
                <p className="text-sm text-amber-600">Review pending items</p>
              </div>
            )}
            {workflow.has_pending_requests && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <FileText className="text-emerald-600 mb-2" size={20} />
                <p className="font-medium text-emerald-800">Document Requests</p>
                <p className="text-sm text-emerald-600">Pending response</p>
              </div>
            )}
            {workflow.has_open_tasks && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <CheckSquare className="text-amber-600 mb-2" size={20} />
                <p className="font-medium text-amber-800">Open Tasks</p>
                <p className="text-sm text-amber-600">Tasks in progress</p>
              </div>
            )}
            {workflow.has_active_disputes && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <Gavel className="text-emerald-600 mb-2" size={20} />
                <p className="font-medium text-emerald-800">Active Disputes</p>
                <p className="text-sm text-emerald-600">Disputes in progress</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs for Different Sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white">Overview</TabsTrigger>
          <TabsTrigger value="timeline" className="data-[state=active]:bg-white">Timeline</TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-white">Documents</TabsTrigger>
          <TabsTrigger value="disputes" className="data-[state=active]:bg-white">Disputes</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-stone-900 mb-4">Case Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Case Information</h4>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Case ID</dt>
                    <dd className="font-mono text-sm">{caseId}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Type</dt>
                    <dd className="capitalize">{caseData?.case_type?.replace('_', ' ') || 'Credit Repair'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Status</dt>
                    <dd className={`px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>{status.label}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Created</dt>
                    <dd>{caseData?.created_at ? new Date(caseData.created_at).toLocaleDateString() : 'N/A'}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <h4 className="font-medium text-slate-700 mb-2">Access Info</h4>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Your Role</dt>
                    <dd className="capitalize">{access?.role?.replace('_', ' ') || 'Staff'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Can Write</dt>
                    <dd>{access?.can_write ? 'Yes' : 'No'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Is Staff</dt>
                    <dd>{access?.is_staff ? 'Yes' : 'No'}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <CaseTimeline caseId={caseId} token={token} />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <CaseDocuments caseId={caseId} token={token} />
        </TabsContent>

        <TabsContent value="disputes" className="mt-6">
          <CaseDisputes caseId={caseId} token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Timeline Component
const CaseTimeline = ({ caseId, token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const response = await axios.get(`${API}/case-modules/case/${caseId}/timeline?limit=20`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEvents(response.data.items || []);
      } catch (err) {
        console.error('Failed to fetch timeline:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTimeline();
  }, [caseId, token]);

  if (loading) {
    return <div className="animate-pulse h-48 bg-slate-100 rounded-xl" />;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="text-lg font-semibold text-stone-900 mb-4">Case Timeline</h3>
      {events.length > 0 ? (
        <div className="space-y-4">
          {events.map((event, index) => (
            <div key={index} className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Activity size={16} className="text-slate-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-stone-900">{event.title}</p>
                {event.description && (
                  <p className="text-sm text-slate-500">{event.description}</p>
                )}
                <p className="text-xs text-slate-400 mt-1">
                  {event.created_at ? new Date(event.created_at).toLocaleString() : ''}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">No timeline events yet</p>
      )}
    </div>
  );
};

// Documents Component
const CaseDocuments = ({ caseId, token }) => {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUploads = async () => {
      try {
        const response = await axios.get(`${API}/case-modules/case/${caseId}/uploads?limit=20`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUploads(response.data.items || []);
      } catch (err) {
        console.error('Failed to fetch uploads:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUploads();
  }, [caseId, token]);

  if (loading) {
    return <div className="animate-pulse h-48 bg-slate-100 rounded-xl" />;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="text-lg font-semibold text-stone-900 mb-4">Case Documents</h3>
      {uploads.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {uploads.map((upload) => (
            <div key={upload.upload_id} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <FileText className="text-slate-400" size={24} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-900 truncate">{upload.file_name}</p>
                  <p className="text-sm text-slate-500">{upload.document_type || 'Document'}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  upload.verification_status === 'verified' 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {upload.verification_status || 'pending'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">No documents uploaded yet</p>
      )}
    </div>
  );
};

// Disputes Component
const CaseDisputes = ({ caseId, token }) => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDisputes = async () => {
      try {
        const response = await axios.get(`${API}/case-modules/case/${caseId}/disputes?limit=20`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDisputes(response.data.items || []);
      } catch (err) {
        console.error('Failed to fetch disputes:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDisputes();
  }, [caseId, token]);

  if (loading) {
    return <div className="animate-pulse h-48 bg-slate-100 rounded-xl" />;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="text-lg font-semibold text-stone-900 mb-4">Case Disputes</h3>
      {disputes.length > 0 ? (
        <div className="space-y-4">
          {disputes.map((dispute) => (
            <div key={dispute.dispute_id} className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-stone-900">Round {dispute.round || 1}</p>
                  <p className="text-sm text-slate-500">{dispute.bureau || 'Multiple Bureaus'}</p>
                  <p className="text-sm text-slate-500">{dispute.items?.length || 0} items</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  dispute.dispute_status === 'resolved' 
                    ? 'bg-emerald-100 text-emerald-700' 
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {dispute.dispute_status || 'pending'}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-500 text-center py-8">No disputes created yet</p>
      )}
    </div>
  );
};

export default StaffCaseDetailPage;
